import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { assistentes as apiAssistentes, type AssistenteNaLista } from '../api/assistentes'
import { ErroApi } from '../api/client'
import { console_ as api, type SessaoDoConsole } from '../api/console'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import './usuarios.css'
import './console.css'

/**
 * Console de teste do assistente (demanda 045).
 *
 * 🔴 **Não é um chat bonito.** O que distingue console de brinquedo é o painel
 * ao lado da resposta: o prompt que foi montado, as ferramentas chamadas, o
 * transbordo, o custo do turno e — principalmente — **o que teria saído e não
 * saiu**. Sem isso, quem ajusta um parâmetro continua iterando às cegas, que é
 * exatamente o problema que a demanda existe para resolver.
 */
export function Console() {
  const { t } = useTranslation()

  const marca = marcaDaRota() ?? ''

  const [lista, setLista] = useState<AssistenteNaLista[] | null>(null)
  const [assistenteId, setAssistenteId] = useState<number | null>(null)
  const [sessao, setSessao] = useState<SessaoDoConsole | null>(null)
  const [prompt, setPrompt] = useState<string | null>(null)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [teto, setTeto] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const quais = await apiAssistentes.listar(marca)
      setLista(quais)

      if (quais.length > 0 && assistenteId === null) setAssistenteId(quais[0].id)
    } catch {
      setFalhou(true)
    }
  }, [marca, assistenteId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  useEffect(() => {
    if (assistenteId === null) return

    let vivo = true

    void api
      .ler(marca, assistenteId)
      .then((s) => vivo && setSessao(s))
      .catch(() => vivo && setFalhou(true))

    // O prompt montado vem da mesma pré-visualização da tela de assistentes: é
    // o texto que o modelo recebeu, e não uma reconstrução para a tela.
    void apiAssistentes
      .prompt(marca, assistenteId)
      .then((p) => vivo && setPrompt(p.prompt))
      .catch(() => vivo && setPrompt(null))

    return () => {
      vivo = false
    }
  }, [marca, assistenteId])

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (assistenteId === null || !texto.trim()) return

    setEnviando(true)
    setErro(null)
    setTeto(null)

    try {
      setSessao(await api.responder(marca, assistenteId, texto))
      setTexto('')
    } catch (falha) {
      // Teto atingido não é erro do console: é o assistente se comportando como
      // se comportaria em produção, e a tela diz qual teto.
      const detalhe = falha instanceof ErroApi ? String((falha.corpo as { detail?: string })?.detail ?? '') : ''

      if (detalhe.toLowerCase().includes('teto')) setTeto(detalhe)
      else setErro(mensagemDeErro(falha, t))
    } finally {
      setEnviando(false)
    }
  }

  async function reiniciar() {
    if (assistenteId === null) return

    await api.reiniciar(marca, assistenteId)
    setSessao(await api.ler(marca, assistenteId))
    setTeto(null)
    setErro(null)
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  if (lista.length === 0)
    return <Estado tipo="vazio" titulo={t('console.semAssistente')} descricao={t('console.semAssistenteDescricao')} />

  const turno = sessao?.turno
  const barrados = (sessao?.envios ?? []).filter((e) => e.barrado)

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('console.titulo')}</h1>
          <p className="usuarios__descricao">{t('console.descricao')}</p>
        </div>

        <Botao variante="secundario" onClick={reiniciar}>
          {t('console.reiniciar')}
        </Botao>
      </header>

      {/* 🔴 O aviso mais importante da tela: nada daqui chega a ninguém. Sem ele,
          quem abre o console pela primeira vez hesita em escrever. */}
      <Aviso tom="info" titulo={t('console.seguro')}>
        {t('console.seguroDescricao')}
      </Aviso>

      <Cartao titulo={t('console.assistente')}>
        <label className="console__escolha">
          <span>{t('console.assistente')}</span>
          <select
            className="campo__entrada"
            value={assistenteId ?? ''}
            onChange={(e) => {
              setAssistenteId(Number(e.target.value))
              setSessao(null)
            }}
          >
            {lista.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome} — {a.modelo}
              </option>
            ))}
          </select>
        </label>
      </Cartao>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {teto && (
        <Aviso tom="alerta" titulo={t('console.tetoAtingido')}>
          {teto}
        </Aviso>
      )}

      <div className="console">
        <Cartao titulo={t('console.conversa')}>
          {!sessao || sessao.mensagens.length === 0 ? (
            <Estado tipo="vazio" titulo={t('console.vazio')} descricao={t('console.vazioDescricao')} />
          ) : (
            <ul className="console__mensagens">
              {sessao.mensagens.map((m) => (
                <li key={m.id} className={`console__mensagem console__mensagem--${m.origem.toLowerCase()}`}>
                  <span className="console__quem">{t(`console.origem.${m.origem.toLowerCase()}`)}</span>
                  <p className="console__texto">{m.texto}</p>
                  <span className="console__quando">{formatar.dataHora(m.em)}</span>
                </li>
              ))}
            </ul>
          )}

          <form className="console__form" onSubmit={enviar}>
            <label className="console__campo">
              <span className="campo__rotulo">{t('console.escreva')}</span>
              <textarea
                className="campo__entrada"
                rows={3}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={t('console.escrevaDica')}
              />
            </label>

            <Botao type="submit" carregando={enviando}>
              {t('console.enviar')}
            </Botao>
          </form>
        </Cartao>

        <Cartao titulo={t('console.diagnostico')}>
          <div className="console__painel">
            <section>
              <h3 className="console__secao">{t('console.custo')}</h3>
              {turno && turno.decisoes.length > 0 ? (
                <ul className="console__dados">
                  <li>
                    {t('console.tokens', {
                      entrada: turno.tokensDeEntrada,
                      saida: turno.tokensDeSaida,
                    })}
                  </li>
                  <li>{t('console.custoDoTurno', { valor: formatar.numero(turno.custo, { minimumFractionDigits: 4 }) })}</li>
                  {turno.decisoes.map((d) => (
                    <li key={d.id}>
                      {d.provedor} · {d.modelo} · {d.duracaoMs} ms
                      {d.usouReserva && ` · ${t('console.reserva')}`}
                      {d.deTeste && ` · ${t('console.marcadoTeste')}`}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="console__nota">{t('console.semTurno')}</p>
              )}
            </section>

            <section>
              <h3 className="console__secao">{t('console.ferramentas')}</h3>
              {turno && turno.ferramentas.length > 0 ? (
                <ul className="console__dados">
                  {turno.ferramentas.map((f, i) => (
                    <li key={i} className="console__passo">
                      <code className="console__ferramenta">{f.ferramenta}</code>
                      {f.pediuTransbordo && (
                        <span className="console__selo">{t('console.pediuTransbordo')}</span>
                      )}

                      {/* 🔴 É este par que faz o console diagnosticar em vez de
                          só narrar: o que o modelo mandou e o que voltou. */}
                      <span className="console__rotuloPasso">{t('console.argumentos')}</span>
                      <pre className="console__json">{f.argumentos}</pre>

                      <span className="console__rotuloPasso">{t('console.retorno')}</span>
                      <pre className="console__json">{f.retorno}</pre>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="console__nota">{t('console.semFerramenta')}</p>
              )}
              <p className="console__nota">{t('console.ferramentasNota')}</p>
            </section>

            <section>
              <h3 className="console__secao">{t('console.transbordo')}</h3>
              {sessao?.transbordos && sessao.transbordos.length > 0 ? (
                <ul className="console__dados">
                  {sessao.transbordos.map((tr) => (
                    <li key={tr.id}>
                      <strong>{tr.motivo}</strong>
                      {tr.time && ` → ${tr.time}`}
                      {tr.causa && <span className="console__nota"> · {tr.causa}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="console__nota">{t('console.semTransbordo')}</p>
              )}
            </section>

            <section>
              {/* 🔴 A seção que justifica o console existir: o que o assistente
                  teria mandado ao cliente, e que morreu na barreira. */}
              <h3 className="console__secao">{t('console.naoEnviado')}</h3>
              {barrados.length > 0 ? (
                <ul className="console__dados">
                  {barrados.map((e) => (
                    <li key={e.id} className="console__barrado">
                      <span className="console__selo">{t('console.selo')}</span>
                      <span className="console__tipoEnvio">{e.tipo}</span>
                      <p className="console__texto">{e.texto}</p>
                      {e.nomeDoAnexo && <span className="console__nota">{e.nomeDoAnexo}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="console__nota">{t('console.semEnvio')}</p>
              )}
            </section>

            <section>
              <h3 className="console__secao">{t('console.prompt')}</h3>
              {prompt ? (
                <pre className="console__prompt">{prompt}</pre>
              ) : (
                <p className="console__nota">{t('console.semPrompt')}</p>
              )}
            </section>
          </div>
        </Cartao>
      </div>
    </section>
  )
}
